import { createRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { QueryDropdown } from 'basic-components';
import { CommonService } from 'dashboard-services/services';
import { DropdownService } from 'primary-components';

import getApiConfig from 'api/ApiConfig';
import properties from 'resources/constants/properties.json';

import { useQueryClient } from '@tanstack/react-query';

const EMPTY_OBJ = {};

const getGroupName = value => {
  if(value?.groupName) {
    return value.groupName
  }
  if(CommonService.isJson(value)) {
    const parsed = JSON.parse(value)
    return parsed?.groupName || undefined
  }
  return value;
}
const EMPTY_ARRAY = [];
const SymbolValue = ({
  onChange,
  value,
  name,
  dependsOnParam = EMPTY_OBJ,
  referenceDependsOnParam = EMPTY_OBJ,
  disabled,
  isMulti = false,
  size = undefined,
  menuPortalTarget = undefined,
  ref,
  ...props
}) => {
  const dispatch = useDispatch(),
        [valueQuery, setValueQuery] = useState(),
        queryClient = useQueryClient(),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        valuesRaw = useMemo(() => {
          if(isMulti) {
            return value !== "" ? String(value).split(',') : EMPTY_ARRAY
          }
          return value !== "" ? String(value) : undefined;
        }, [isMulti, value]),
        symbolKey = useMemo(() => dependsOnParam?.value || dependsOnParam?.defaultValue, [dependsOnParam]),
        groupName = useMemo(() => getGroupName(referenceDependsOnParam?.value || referenceDependsOnParam?.defaultValue), [referenceDependsOnParam?.defaultValue, referenceDependsOnParam?.value]),

        { fetchNextPage: fetchMoreSymbolValues, isFetching: isFetchingValues, isFetchingNextPage: isFetchingNextPageValues, data: valuesFromApi = EMPTY_ARRAY } = DropdownService.useQuerySymbolValues({
          queryClient,
          getApiConfig: getApiConfigLocal,
          input: valueQuery,
          key: symbolKey,
          groupName,
          useEs: properties.useEsSymbolListing === "true",
          enabled: !!symbolKey,
          metadata: true,
        }),
        valuesRef = useMemo(() => ref || createRef(), [ref]),
        values = useMemo(() => isMulti ? valuesRaw?.map(c => ({ label: c, value: c })) : valuesRaw, [isMulti, valuesRaw])

  useEffect(() => {
    valuesRef.current?.refreshOptions?.()
  }, [symbolKey, valuesRef])

  useImperativeHandle(ref, () => ({ 
    validateForForm: () => !!value,
    isValid: () => !!value
  }))

  return (
    <QueryDropdown
        {...props}
        defaultValue={values}
        disabled={!symbolKey || disabled}
        fetchNextPage={fetchMoreSymbolValues}
        isFetching={isFetchingValues || isFetchingNextPageValues}
        menuPortalTarget={menuPortalTarget || document.body}
        multi={isMulti}
        name={name}
        onChange={onChange}
        onInputChange={setValueQuery}
        options={valuesFromApi}
        ref={valuesRef}
        size={size}
    />
  )
}

SymbolValue.displayName = "SymbolValue"

SymbolValue.propTypes = {
  dependsOnParam: PropTypes.object, 
  disabled: PropTypes.bool.isRequired, 
  isMulti: PropTypes.bool, 
  menuPortalTarget: PropTypes.any, 
  name: PropTypes.string.isRequired, 
  onChange: PropTypes.func.isRequired,
  referenceDependsOnParam: PropTypes.object, 
  size: PropTypes.string,
  value: PropTypes.any.isRequired
}

export default SymbolValue;
